import { useEffect, useMemo, useRef, useState } from "react";
import type { LiveTranscriptLine } from "../types/liveTranscript";

// Voice Messaging–style karaoke timing for live video.
const CHARS_PER_SEC = 16;
const MIN_DURATION_SEC = 1.0;
const LEAD_SEC = 0.35;
const MAX_WAIT_FOR_AUDIO_MS = 2800;
const STOP_COMMIT_GRACE_MS = 900;

export type KaraokeTranscriptLine = LiveTranscriptLine & {
  currentTimeSec: number;
  durationSec: number;
  isPlaying: boolean;
  isComplete: boolean;
};

const estimateDurationSec = (text: string): number => {
  const chars = Math.max(1, text.replace(/\s+/g, " ").trim().length);
  return Math.max(MIN_DURATION_SEC, chars / CHARS_PER_SEC);
};

type TurnPlayback = {
  currentTimeSec: number;
  durationSec: number;
  committed: boolean;
  playing: boolean;
};

export const useAudioSyncedTranscript = (
  lines: LiveTranscriptLine[],
  isAiSpeaking: boolean,
): KaraokeTranscriptLine[] => {
  const [playback, setPlayback] = useState<Record<string, TurnPlayback>>({});
  const [nowMs, setNowMs] = useState(() => Date.now());

  const isAiSpeakingRef = useRef(isAiSpeaking);
  const wasAiSpeakingRef = useRef(isAiSpeaking);
  const linesRef = useRef(lines);
  const playbackRef = useRef(playback);
  const speechStartedAtRef = useRef<number | null>(null);
  const activeTurnIdRef = useRef<string | null>(null);
  const waitTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const stopCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const rafRef = useRef<number | null>(null);

  isAiSpeakingRef.current = isAiSpeaking;
  linesRef.current = lines;
  playbackRef.current = playback;

  const pickActiveAssistantTurn = (): LiveTranscriptLine | null => {
    return (
      linesRef.current.find(
        (l) =>
          l.role === "assistant" &&
          !playbackRef.current[l.turnId]?.committed,
      ) ?? null
    );
  };

  useEffect(() => {
    if (lines.length > 0) return;
    setPlayback({});
    speechStartedAtRef.current = null;
    activeTurnIdRef.current = null;
    waitTimersRef.current.forEach((t) => clearTimeout(t));
    waitTimersRef.current.clear();
    if (stopCommitTimerRef.current) {
      clearTimeout(stopCommitTimerRef.current);
      stopCommitTimerRef.current = null;
    }
  }, [lines.length]);

  // Late assistant text with no AI audio → mark complete after short wait.
  useEffect(() => {
    for (const line of lines) {
      if (line.role !== "assistant") continue;
      if (playback[line.turnId]?.committed) continue;
      if (waitTimersRef.current.has(line.turnId)) continue;
      if (isAiSpeaking) continue;

      const turnId = line.turnId;
      const timer = setTimeout(() => {
        waitTimersRef.current.delete(turnId);
        if (isAiSpeakingRef.current) return;
        setPlayback((prev) => {
          if (prev[turnId]?.committed) return prev;
          const text =
            linesRef.current.find((l) => l.turnId === turnId)?.text ?? "";
          const durationSec = estimateDurationSec(text);
          return {
            ...prev,
            [turnId]: {
              currentTimeSec: durationSec,
              durationSec,
              committed: true,
              playing: false,
            },
          };
        });
      }, MAX_WAIT_FOR_AUDIO_MS);
      waitTimersRef.current.set(turnId, timer);
    }
  }, [lines, playback, isAiSpeaking]);

  useEffect(() => {
    const started = isAiSpeaking && !wasAiSpeakingRef.current;
    const stopped = !isAiSpeaking && wasAiSpeakingRef.current;
    wasAiSpeakingRef.current = isAiSpeaking;

    if (started) {
      waitTimersRef.current.forEach((t) => clearTimeout(t));
      waitTimersRef.current.clear();
      if (stopCommitTimerRef.current) {
        clearTimeout(stopCommitTimerRef.current);
        stopCommitTimerRef.current = null;
      }

      // Always arm the speech clock immediately — transcript often arrives late.
      if (!speechStartedAtRef.current) {
        speechStartedAtRef.current = Date.now();
      }

      const active = pickActiveAssistantTurn();
      if (active) {
        const existing = playbackRef.current[active.turnId];
        const elapsedSec = speechStartedAtRef.current
          ? (Date.now() - speechStartedAtRef.current) / 1000
          : 0;
        // Resume same turn after a brief pause — keep clock, don't restart.
        if (existing?.playing || (existing && !existing.committed)) {
          activeTurnIdRef.current = active.turnId;
          setPlayback((prev) => ({
            ...prev,
            [active.turnId]: {
              ...prev[active.turnId],
              playing: true,
              committed: false,
            },
          }));
        } else {
          activeTurnIdRef.current = active.turnId;
          const durationSec = estimateDurationSec(active.text);
          setPlayback((prev) => ({
            ...prev,
            [active.turnId]: {
              currentTimeSec: Math.min(elapsedSec + LEAD_SEC, durationSec),
              durationSec,
              committed: false,
              playing: true,
            },
          }));
        }
      }
    }

    if (stopped) {
      // Grace period: sentence pauses should not finalize karaoke early.
      if (stopCommitTimerRef.current) {
        clearTimeout(stopCommitTimerRef.current);
      }
      stopCommitTimerRef.current = setTimeout(() => {
        stopCommitTimerRef.current = null;
        if (isAiSpeakingRef.current) return;

        const turnId = activeTurnIdRef.current;
        speechStartedAtRef.current = null;
        activeTurnIdRef.current = null;

        setPlayback((prev) => {
          let changed = false;
          const next = { ...prev };

          const commit = (id: string, text: string) => {
            const durationSec =
              next[id]?.durationSec ?? estimateDurationSec(text);
            next[id] = {
              currentTimeSec: durationSec,
              durationSec,
              committed: true,
              playing: false,
            };
            changed = true;
          };

          if (turnId) {
            const text =
              linesRef.current.find((l) => l.turnId === turnId)?.text ?? "";
            commit(turnId, text);
          }

          for (const line of linesRef.current) {
            if (line.role !== "assistant") continue;
            if (next[line.turnId]?.committed) continue;
            if (next[line.turnId]?.playing || next[line.turnId]) {
              commit(line.turnId, line.text);
            }
          }

          return changed ? next : prev;
        });
      }, STOP_COMMIT_GRACE_MS);
    }
  }, [isAiSpeaking]);

  // Text arrives while AI already speaking — catch up to elapsed speech time.
  useEffect(() => {
    if (!isAiSpeaking) return;
    const active = pickActiveAssistantTurn();
    if (!active) return;
    if (playback[active.turnId]?.playing || playback[active.turnId]?.committed) {
      return;
    }

    activeTurnIdRef.current = active.turnId;
    if (!speechStartedAtRef.current) {
      speechStartedAtRef.current = Date.now();
    }
    const elapsedSec = (Date.now() - speechStartedAtRef.current) / 1000;
    const durationSec = estimateDurationSec(active.text);
    setPlayback((prev) => ({
      ...prev,
      [active.turnId]: {
        currentTimeSec: Math.min(elapsedSec + LEAD_SEC, durationSec * 0.95),
        durationSec,
        committed: false,
        playing: true,
      },
    }));
  }, [lines, isAiSpeaking, playback]);

  useEffect(() => {
    if (!isAiSpeaking) {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    const tick = () => {
      const startedAt = speechStartedAtRef.current;
      const turnId = activeTurnIdRef.current;
      if (startedAt != null && turnId) {
        const elapsedSec = (Date.now() - startedAt) / 1000 + LEAD_SEC;
        setPlayback((prev) => {
          const cur = prev[turnId];
          if (!cur || cur.committed) return prev;
          const text =
            linesRef.current.find((l) => l.turnId === turnId)?.text ?? "";
          let durationSec = cur.durationSec || estimateDurationSec(text);
          // Stretch only when speech clearly outlasts estimate — keep highlight
          // near the end rather than freezing early.
          if (elapsedSec > durationSec * 0.92) {
            durationSec = Math.max(durationSec, elapsedSec / 0.92);
          }
          return {
            ...prev,
            [turnId]: {
              ...cur,
              currentTimeSec: elapsedSec,
              durationSec,
              playing: true,
            },
          };
        });
        setNowMs(Date.now());
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isAiSpeaking]);

  useEffect(() => {
    setPlayback((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const line of lines) {
        if (line.role !== "assistant") continue;
        const cur = next[line.turnId];
        if (!cur || cur.committed) continue;
        const estimated = estimateDurationSec(line.text);
        if (estimated > cur.durationSec) {
          next[line.turnId] = { ...cur, durationSec: estimated };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [lines]);

  return useMemo(() => {
    return lines.map((line) => {
      if (line.role === "user") {
        return {
          ...line,
          currentTimeSec: 0,
          durationSec: 0,
          isPlaying: false,
          isComplete: line.isFinal,
        };
      }

      const state = playback[line.turnId];
      if (!state) {
        // Full text visible as "future" until playback starts.
        return {
          ...line,
          currentTimeSec: 0,
          durationSec: estimateDurationSec(line.text),
          isPlaying: false,
          isComplete: false,
        };
      }

      return {
        ...line,
        currentTimeSec: state.currentTimeSec,
        durationSec: state.durationSec,
        isPlaying: state.playing,
        isComplete: state.committed,
      };
    });
  }, [lines, playback, nowMs]);
};
